import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Lock, Loader2, Check, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPassword({ token, onComplete }) {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [showPass, setShowPass] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error("La contraseña debe tener al menos 6 caracteres");
      return;
    }
    if (password !== confirm) {
      toast.error("Las contraseñas no coinciden");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, password }),
      });
      if (res.ok) {
        setDone(true);
        toast.success("Contraseña actualizada");
      } else {
        const data = await res.json().catch(() => ({}));
        toast.error(data.detail || "Error al restablecer la contraseña");
      }
    } catch {
      toast.error("Error de conexión");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="text-center space-y-4" data-testid="reset-password-success">
        <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto">
          <Check className="w-6 h-6 text-green-600" />
        </div>
        <h3 className="text-lg font-heading font-semibold text-[#1B2B4D]">Contraseña actualizada</h3>
        <p className="text-sm text-[#4A5568]">Tu contraseña ha sido restablecida correctamente.</p>
        <Button onClick={onComplete} className="bg-[#0066FF] hover:bg-[#0052CC]" data-testid="go-to-login-btn">
          Iniciar sesión
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="reset-password-form">
      <div className="text-center mb-2">
        <h3 className="text-lg font-heading font-semibold text-[#1B2B4D]">Nueva contraseña</h3>
        <p className="text-sm text-[#4A5568] mt-1">Ingresa tu nueva contraseña.</p>
      </div>
      <div>
        <Label htmlFor="new-password" className="text-[#4A5568] text-sm">Nueva contraseña</Label>
        <div className="relative mt-1">
          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A0AEC0]" />
          <Input
            id="new-password"
            type={showPass ? "text" : "password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Mínimo 6 caracteres"
            className="pl-10 pr-10"
            required
            data-testid="new-password-input"
          />
          <button type="button" onClick={() => setShowPass(!showPass)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A0AEC0] hover:text-[#4A5568]">
            {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>
      </div>
      <div>
        <Label htmlFor="confirm-password" className="text-[#4A5568] text-sm">Confirmar contraseña</Label>
        <div className="relative mt-1">
          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A0AEC0]" />
          <Input
            id="confirm-password"
            type={showPass ? "text" : "password"}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder="Repite la contraseña"
            className="pl-10"
            required
            data-testid="confirm-password-input"
          />
        </div>
      </div>
      <Button type="submit" disabled={loading} className="w-full bg-[#0066FF] hover:bg-[#0052CC]" data-testid="reset-submit-btn">
        {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
        Restablecer contraseña
      </Button>
    </form>
  );
}
